import { type AgentInboundMessagePill } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

import { formatContextSourceFieldValueMonikerId } from '~/components/v2/components/workspace/workspace-agent-panel/adapters/durable-plain-text/contextSourceFieldValueMonikerId';
import {
  readWorkspaceAgentPillNodeAttrs,
  WORKSPACE_AGENT_PILL_NODE_NAME,
  type WorkspaceAgentPillDocument,
  type WorkspaceAgentPillNodeAttrs,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';
import {
  readWorkspaceAgentSkillMonikerAttrs,
  WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentSkillMoniker';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

export type WorkspaceAgentComposerSerializeOptions = {
  resolveSkill: (skillId: string) => Skill | undefined;
};

export type WorkspaceAgentComposerSerializeResult = {
  content: string;
  pills: AgentInboundMessagePill[];
};

/** Structural minimum of a TipTap editor, so tests can pass a plain object. */
export type WorkspaceAgentComposerDoc = {
  state: { doc: WorkspaceAgentPillDocument };
};

const PARAGRAPH_NODE_NAME = 'paragraph';
const HARD_BREAK_NODE_NAME = 'hardBreak';

/** The only pill shapes an editor node can build — never `assetList` / `boardList`, which uncited scope selection collapses to on send. */
type WorkspaceAgentComposerNodePill = Extract<
  AgentInboundMessagePill,
  { type: 'asset' | 'board' | 'contextSourceFieldValue' }
>;

/**
 * User line breaks carry meaning for the agent, so `\n`s survive; per-line trim drops the trailing
 * space every pill moniker ends with.
 */
const normalizeWorkspaceAgentComposerWhitespace = (raw: string): string =>
  raw
    .split('\n')
    .map((line) => line.replace(/[^\S\n]+/g, ' ').trim())
    .join('\n')
    .trim();

/** Shared with `hasSendableWorkspaceAgentComposerContent`, so what arms Send is exactly what can be sent. */
export const buildWorkspaceAgentComposerNodePill = (
  attrs: WorkspaceAgentPillNodeAttrs,
): WorkspaceAgentComposerNodePill | null => {
  if (attrs.type === 'board') {
    return attrs.boardId ? { id: attrs.id, type: 'board', boardId: attrs.boardId } : null;
  }

  if (attrs.type === 'contextSourceFieldValue') {
    return attrs.contextSourceId && attrs.contextSourceFieldValueId
      ? {
          id: formatContextSourceFieldValueMonikerId({
            contextSourceFieldValueId: attrs.contextSourceFieldValueId,
            contextSourceId: attrs.contextSourceId,
          }),
          type: 'contextSourceFieldValue',
          contextSourceId: attrs.contextSourceId,
          contextSourceFieldValueId: attrs.contextSourceFieldValueId,
        }
      : null;
  }

  if (!attrs.assetId) {
    return null;
  }

  return {
    // A versionless mention is addressed by asset id. A saved version keeps the client id; the
    // moniker carries the version id, which is what a `{type: "asset", origin: "pill"}` handle uses.
    id: attrs.versionId ? attrs.id : attrs.assetId,
    type: 'asset',
    assetId: attrs.assetId,
    ...(attrs.versionId ? { versionId: attrs.versionId } : {}),
  };
};

/** Trailing space matches the skill moniker so per-line trim can drop it when the chip ends the line. */
const workspaceAgentComposerEntityMoniker = (pill: WorkspaceAgentComposerNodePill): string => {
  if (pill.type === 'board') {
    return `@[board:${pill.boardId}] `;
  }

  if (pill.type === 'contextSourceFieldValue') {
    return `@[contextSourceFieldValue:${pill.id}] `;
  }

  if (pill.versionId) {
    return `@[assetVersion:${pill.versionId}] `;
  }

  return `@[asset:${pill.assetId}] `;
};

/** Two mentions of one asset collapse to a single pill; two versions of it do not. */
const getWorkspaceAgentComposerPillReferenceKey = (pill: WorkspaceAgentComposerNodePill): string => {
  if (pill.type === 'board') {
    return `board:${pill.boardId}`;
  }

  // The kit as well as the value: the same file under two kits is two references.
  if (pill.type === 'contextSourceFieldValue') {
    return `contextSourceFieldValue:${pill.contextSourceId}:${pill.contextSourceFieldValueId}`;
  }

  return `asset:${pill.assetId}:${pill.versionId ?? ''}`;
};

/** Wire-format `content`: each pill is an entity moniker. `@[pill:]` stays parseable for older messages. */
export const serializeWorkspaceAgentComposerContent = (
  editor: WorkspaceAgentComposerDoc,
  options: WorkspaceAgentComposerSerializeOptions,
): WorkspaceAgentComposerSerializeResult => {
  const pills: AgentInboundMessagePill[] = [];
  const seenReferences = new Set<string>();
  const parts: string[] = [];
  let hasSeenParagraph = false;

  // Return `true` once at the end: pills and text are leaves, so nothing here stops the walk.
  editor.state.doc.descendants((node) => {
    if (node.type.name === PARAGRAPH_NODE_NAME) {
      if (hasSeenParagraph) {
        parts.push('\n');
      }

      hasSeenParagraph = true;
    } else if (node.type.name === HARD_BREAK_NODE_NAME) {
      parts.push('\n');
    } else if (node.type.name === WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME) {
      const skillAttrs = readWorkspaceAgentSkillMonikerAttrs(node.attrs);

      if (skillAttrs) {
        const resolvedSkill = options.resolveSkill(skillAttrs.skillId);

        if (resolvedSkill) {
          parts.push(`@[skill:${resolvedSkill.id}] `);
        } else {
          reportErrorToBugsnag({
            context: 'serializeWorkspaceAgentComposerContent',
            error: new Error('Composer skill moniker is not in the workspace catalog and cannot be sent'),
            metadata: { skillId: skillAttrs.skillId },
          });
        }
      }
    } else if (node.type.name === WORKSPACE_AGENT_PILL_NODE_NAME) {
      const attrs = readWorkspaceAgentPillNodeAttrs(node.attrs);
      const pill = attrs ? buildWorkspaceAgentComposerNodePill(attrs) : null;

      // `parseHTML.getAttrs` already rejects unusable marker spans, so an unreadable node means the
      // composer and node definition diverged. Report rather than silently drop a visible mention.
      if (!pill) {
        // No label/thumbnail in the report: workspace content.
        reportErrorToBugsnag({
          error: new Error('Composer pill cannot be read as a reference and cannot be sent'),
          context: 'serializeWorkspaceAgentComposerContent',
          // Read off the raw node, not the parsed attrs: those are null for exactly the nodes being
          // reported, which would blank out the unrecognized `type` the report exists to carry.
          metadata: {
            hasAssetId: !!node.attrs.assetId,
            hasBoardId: !!node.attrs.boardId,
            type: typeof node.attrs.type === 'string' ? node.attrs.type : null,
          },
        });
      } else {
        // A reference mentioned twice is one pill on the wire. The moniker names the entity, so both
        // mentions already agree without borrowing the first node's client id.
        const referenceKey = getWorkspaceAgentComposerPillReferenceKey(pill);

        if (!seenReferences.has(referenceKey)) {
          seenReferences.add(referenceKey);
          pills.push(pill);
        }

        parts.push(workspaceAgentComposerEntityMoniker(pill));
      }
    } else if (node.isText && typeof node.text === 'string') {
      parts.push(node.text);
    }

    return true;
  });

  return { content: normalizeWorkspaceAgentComposerWhitespace(parts.join('')), pills };
};
